import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Search, Star, Loader2, Users, Filter, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { SEO, createBreadcrumbSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTeachers, useTeacherSpecialties, useTeacherLanguages } from '@/hooks/use-public-api';
import { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';
import EmptyState from '@/components/common/EmptyState';
import { TeacherGridCardSkeleton } from '@/components/common/LoadingSkeleton';
import { specialtyLabel } from '@/lib/specialties';
import { useCurrency } from '@/contexts/CurrencyContext';
import { motion } from 'framer-motion';

interface TeacherType {
  slug?: string;
  name: string;
  full_name: string;
  full_name_ar?: string;
  profile_image?: string;
  bio?: string;
  bio_ar?: string;
  specialties?: string;
  specialties_list?: string[];
  teaching_languages?: string;
  languages_list?: string[];
  hourly_rate: number;
  currency: string;
  years_experience?: number;
  average_rating: number;
  total_reviews?: number;
  students_taught?: number;
  total_sessions?: number;
  country?: string;
}

const TeacherCard = ({ teacher, isRTL }: { teacher: TeacherType; isRTL: boolean }) => {
  const { formatPrice } = useCurrency();
  const name = isRTL && teacher.full_name_ar ? teacher.full_name_ar : teacher.full_name;
  const bio = isRTL && teacher.bio_ar ? teacher.bio_ar : teacher.bio;
  const specialties = teacher.specialties_list || (teacher.specialties ? teacher.specialties.split(',').map(s => s.trim()) : []);

  return (
    <Link to={`/teachers/${encodeURIComponent(teacher.slug || teacher.name)}`}>
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 group h-full flex flex-col"
      >
        {/* Image */}
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          <img
            src={teacher.profile_image || ''}
            alt={name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          {!teacher.profile_image && (
            <div className="w-full h-full flex items-center justify-center bg-primary/10">
              <span className="text-4xl font-arabic font-bold text-primary">{name.charAt(0)}</span>
            </div>
          )}
        </div>

        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-semibold text-foreground text-lg mb-1 truncate">{name}</h3>

          {/* Rating */}
          <div
            className="flex items-center gap-1.5 mb-3"
            aria-label={isRTL
              ? `${(teacher.average_rating || 0).toFixed(1)} من 5 نجوم${teacher.total_reviews ? `، ${teacher.total_reviews} تقييم` : ''}`
              : `${(teacher.average_rating || 0).toFixed(1)} out of 5 stars${teacher.total_reviews ? `, ${teacher.total_reviews} reviews` : ''}`}
          >
            <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />
            <span aria-hidden="true" className="text-sm font-medium text-foreground">{(teacher.average_rating || 0).toFixed(1)}</span>
            {teacher.total_reviews ? (
              <span aria-hidden="true" className="text-xs text-muted-foreground">({teacher.total_reviews})</span>
            ) : null}
          </div>

          {/* Bio */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-3 truncate-2 flex-1">
            {bio ? bio.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 150) : ''}
          </p>

          {/* Specialties */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {specialties.slice(0, 3).map((s) => (
              <span key={s} className="px-2.5 py-1 bg-primary/8 text-primary text-xs font-medium rounded-md">
                {specialtyLabel(s, isRTL)}
              </span>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
            <div>
              <span className="text-lg font-bold text-foreground">{formatPrice(teacher.hourly_rate || 0)}</span>
              <span className="text-xs text-muted-foreground ms-1">/{isRTL ? 'ساعة' : 'hr'}</span>
            </div>
            <span className="text-xs font-medium text-primary group-hover:underline flex items-center gap-1">
              {isRTL ? 'عرض الملف' : 'View Profile'}
              <ArrowRight className="h-3 w-3 rtl-mirror" />
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

const TeachersPage = () => {
  const { language, isRTL, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [selectedPriceRange, setSelectedPriceRange] = useState<string>('');
  const [page, setPage] = useState(1);

  const getPriceFilters = () => {
    switch (selectedPriceRange) {
      case '0-25': return { min_price: 0, max_price: 25 };
      case '25-50': return { min_price: 25, max_price: 50 };
      case '50-100': return { min_price: 50, max_price: 100 };
      case '100+': return { min_price: 100, max_price: undefined };
      default: return { min_price: undefined, max_price: undefined };
    }
  };

  const priceFilters = getPriceFilters();

  const { data: teachersData, isLoading, error } = useTeachers({
    search: searchQuery || undefined,
    specialty: selectedSpecialty || undefined,
    min_price: priceFilters.min_price,
    max_price: priceFilters.max_price,
    page,
    limit: 12,
  });

  const { data: specialties } = useTeacherSpecialties();

  const teachers = teachersData?.teachers || [];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createBreadcrumbSchema(
        [
          { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
          { name: t('nav.teachers'), url: 'https://hudanlilmuttaqeen.com/teachers' }
        ],
        language === 'ar' ? 'ar' : 'en'
      ),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "inLanguage": language === 'ar' ? 'ar' : 'en',
        "itemListElement": teachers.map((teacher: TeacherType, idx: number) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "url": `https://hudanlilmuttaqeen.com/teachers/${teacher.name}`,
          "name": isRTL && teacher.full_name_ar ? teacher.full_name_ar : teacher.full_name
        }))
      }
    ]
  };

  return (
    <Layout>
      <SEO
        titleKey="seo.teachers.title"
        descriptionKey="seo.teachers.description"
        keywordsKey="seo.teachers.keywords"
        structuredData={structuredData}
      />

      <PageHeader
        title={isRTL ? 'معلمونا المميزون' : 'Our Teachers'}
        subtitle={isRTL ? 'اعثر على المعلم المثالي لرحلتك القرآنية. جميع معلمينا حاصلون على إجازات معتمدة.' : 'Find the perfect teacher for your Quranic journey. All our teachers hold verified Ijazah certifications.'}
        pattern="geometric"
        size="sm"
      />

      <section className="section-sm bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-5 mb-8 shadow-soft">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-4">
              <Filter className="h-4 w-4" />
              {isRTL ? 'تصفية النتائج' : 'Filter Results'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className={`absolute ${isRTL ? 'end-3' : 'start-3'} top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4`} aria-hidden="true" />
                <Input
                  className={`${isRTL ? 'pe-10' : 'ps-10'} rounded-lg`}
                  placeholder={isRTL ? 'ابحث بالاسم أو التخصص...' : 'Search by name or specialty...'}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  aria-label={isRTL ? 'البحث عن المعلمين' : 'Search teachers'}
                  type="search"
                />
              </div>
              <Select value={selectedSpecialty || 'all'} onValueChange={(v) => { setSelectedSpecialty(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder={isRTL ? 'التخصص' : 'Specialty'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع التخصصات' : 'All Specialties'}</SelectItem>
                  {specialties?.map((specialty) => (
                    <SelectItem key={specialty} value={specialty}>{specialtyLabel(specialty, isRTL)}</SelectItem>
                  ))}
                  {!specialties?.length && (
                    <>
                      <SelectItem value="tajweed">{isRTL ? 'التجويد' : 'Tajweed'}</SelectItem>
                      <SelectItem value="hifz">{isRTL ? 'الحفظ' : 'Hifz'}</SelectItem>
                      <SelectItem value="tafsir">{isRTL ? 'التفسير' : 'Tafsir'}</SelectItem>
                      <SelectItem value="arabic">{isRTL ? 'اللغة العربية' : 'Arabic'}</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              <Select value={selectedPriceRange || 'all'} onValueChange={(v) => { setSelectedPriceRange(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder={isRTL ? 'نطاق السعر' : 'Price Range'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الأسعار' : 'All Prices'}</SelectItem>
                  <SelectItem value="0-25">$0 - $25</SelectItem>
                  <SelectItem value="25-50">$25 - $50</SelectItem>
                  <SelectItem value="50-100">$50 - $100</SelectItem>
                  <SelectItem value="100+">$100+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <TeacherGridCardSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={Users}
              title={isRTL ? 'خطأ في تحميل المعلمين' : 'Error loading teachers'}
              description={isRTL ? 'يرجى المحاولة مرة أخرى لاحقًا' : 'Please try again later'}
            />
          ) : teachers.length === 0 ? (
            <EmptyState
              icon={Users}
              title={isRTL ? 'لا يوجد معلمين' : 'No teachers found'}
              description={isRTL ? 'جرب تغيير معايير البحث' : 'Try adjusting your search filters'}
            />
          ) : (
            <>
              <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {teachers.map((teacher: TeacherType) => (
                  <StaggerItem key={teacher.name}>
                    <TeacherCard teacher={teacher} isRTL={isRTL} />
                  </StaggerItem>
                ))}
              </StaggerContainer>

              {teachersData && teachersData.pages > 1 && (
                <div className="mt-10 space-y-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      aria-label={isRTL ? 'الصفحة السابقة' : 'Previous page'}
                    >
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm text-muted-foreground px-3">
                      {page} / {teachersData.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(teachersData.pages, p + 1))}
                      disabled={page >= teachersData.pages}
                      aria-label={isRTL ? 'الصفحة التالية' : 'Next page'}
                    >
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isRTL
                      ? `عرض ${teachers.length} من ${teachersData.total} معلم`
                      : `Showing ${teachers.length} of ${teachersData.total} teachers`
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default TeachersPage;
